import { Drawer } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FilterFlexpools } from '../../filters/inputs/flexpools/filter-flexpools';
import { FilterWrapper } from '../../filters/filter-wrapper';
import { CompanyFlexpools } from '../../../../api/services/availability/availability-service.schema';
import { LwAutocomplete } from '../../../../redesign';
import { FlexpoolsFilterFormContext } from '../../filters/inputs/flexpools/filter-flexpools.types';
import { useSiteDetection } from '../../../hooks';

export const AvailabilityFilters = ({
  companyFlexPools,
  companyInput,
  isOpen,
  companies,
  company,
  toggleFilters,
  handleCancel,
  handleFiltersChange,
  setCompany,
  setCompanyInput,
  currentFilters,
}: {
  companyFlexPools: CompanyFlexpools;
  companies: { id: string; name: string }[];
  isOpen: boolean;
  company?: { value: string; label: string };
  toggleFilters: () => void;
  handleCancel: () => void;
  setCompany: ({ value, label }?: { value: string; label: string }) => void;
  setCompanyInput: (value: string) => void;
  handleFiltersChange: (flexPoolIds: string[]) => void;
  currentFilters: string[];
  companyInput: string;
}) => {
  const form = useForm<FlexpoolsFilterFormContext>({
    defaultValues: {
      flexpools: currentFilters.map((id) => {
        const matchingFlexpoolFromQuery = companyFlexPools.flexPools.find(
          (flexPool) => flexPool.id === id
        );
        return matchingFlexpoolFromQuery
          ? { value: id, label: matchingFlexpoolFromQuery.name }
          : undefined;
      }),
    },
  });
  const { t } = useTranslation('web-common');
  const { site } = useSiteDetection();
  const isOgp = site === 'ogp';
  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.name,
      })),
    [companies]
  );

  const flexPoolOptions = useMemo(
    () =>
      companyFlexPools.flexPools.map((flexPool) => ({
        value: flexPool.id,
        label: flexPool.name,
      })),
    [companyFlexPools]
  );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      form.handleSubmit((data) => {
        handleFiltersChange(data.flexpools.map((flexPool) => flexPool.value));
        toggleFilters();
      })();
    },
    [form, handleFiltersChange]
  );
  const handleClear = useCallback(() => {
    form.reset({
      flexpools: [],
    });
    handleFiltersChange([]);
    setCompany(undefined);
  }, [form, handleFiltersChange]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={handleCancel}
          onClear={handleClear}
          onSubmit={handleSubmit}
        >
          {!isOgp && (
            <LwAutocomplete
              name="company"
              isLoading={false}
              value={company ?? null}
              label={t('company-filter.autocomplete.label')}
              getOptionLabel={(value) => value?.label ?? ''}
              isOptionEqualToValue={(option, value) => {
                return option?.value === value?.value;
              }}
              noOptionsText={t('company-filter.autocomplete.min-chars', { count: 3 })}
              inputValue={companyInput ?? ''}
              onInputChange={(_event, value) => {
                setCompanyInput(value);
              }}
              getOptionKey={(option) => option.value}
              options={companyOptions}
              onChange={(_event, option) => {
                form.setValue('flexpools', []);
                if (option?.value && option.label) {
                  setCompany({ value: option.value, label: option.label });
                }
              }}
              error={false}
              helperText={undefined}
            />
          )}
          <FilterFlexpools
            companyFlexpools={flexPoolOptions}
            disabled={!isOgp && !company?.value}
          />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
